import {
  Download,
  FileArchive,
  FileUp,
  LoaderCircle,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import { useState, type FormEvent } from 'react'

import { Button } from '../../components/ui/Button'
import { PageHeader } from '../../components/ui/PageHeader'
import { useMaterials } from '../../hooks/useMaterials'
import {
  downloadMaterial,
  MATERIAL_CATEGORIES,
  type MaterialCategory,
} from '../../lib/materialDb'

function formatFileSize(size: number) {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / 1024 / 1024).toFixed(1)} MB`
}

export function MaterialsPage() {
  const { records, status, error, add, remove, refresh } = useMaterials()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [category, setCategory] = useState<MaterialCategory>('最近试卷')
  const [isSaving, setIsSaving] = useState(false)
  const [formError, setFormError] = useState('')

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!selectedFile) {
      setFormError('请选择需要保存的资料。')
      return
    }

    setIsSaving(true)
    setFormError('')
    try {
      await add(selectedFile, category)
      setSelectedFile(null)
      event.currentTarget.reset()
    } catch (saveError) {
      setFormError(saveError instanceof Error ? saveError.message : '资料保存失败')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="content-page">
      <PageHeader
        title="动态资料库"
        description="保存教材、作业、小测和试卷，后续用于知识点标注与错因分析。"
      />

      <div className="materials-layout">
        <form className="upload-panel" onSubmit={handleSubmit}>
          <div className="upload-panel__heading">
            <FileUp size={24} strokeWidth={1.6} aria-hidden="true" />
            <div>
              <h2>上传新资料</h2>
              <p>首版资料只保存在当前浏览器，不会自动上传到云端。</p>
            </div>
          </div>

          <label htmlFor="material-category">资料分类</label>
          <select
            id="material-category"
            value={category}
            onChange={(event) =>
              setCategory(event.target.value as MaterialCategory)
            }
          >
            {MATERIAL_CATEGORIES.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          <label htmlFor="material-file">选择文件</label>
          <input
            id="material-file"
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt,.md"
            onChange={(event) => setSelectedFile(event.target.files?.[0] ?? null)}
          />
          {selectedFile ? (
            <p className="selected-file">
              已选择：{selectedFile.name} · {formatFileSize(selectedFile.size)}
            </p>
          ) : null}
          {formError ? <p className="form-error">{formError}</p> : null}

          <Button
            type="submit"
            variant="teal"
            disabled={isSaving}
            leadingIcon={
              isSaving ? (
                <LoaderCircle className="spin" size={17} />
              ) : (
                <FileUp size={17} />
              )
            }
          >
            {isSaving ? '正在保存' : '保存到本地资料库'}
          </Button>
        </form>

        <section className="materials-list" aria-labelledby="saved-materials-heading">
          <div className="section-heading">
            <div>
              <h2 id="saved-materials-heading">已保存资料</h2>
              <p>{records.length} 份资料保存在当前设备。</p>
            </div>
            <button
              type="button"
              className="icon-button"
              aria-label="刷新资料列表"
              onClick={() => void refresh()}
            >
              <RefreshCw size={18} aria-hidden="true" />
            </button>
          </div>

          {status === 'loading' && records.length === 0 ? (
            <div className="loading-state" role="status">
              <LoaderCircle className="spin" size={24} aria-hidden="true" />
              正在读取本地资料……
            </div>
          ) : null}

          {status === 'error' ? (
            <div className="inline-error" role="alert">
              <strong>无法读取资料库</strong>
              <span>{error}</span>
              <Button variant="secondary" onClick={() => void refresh()}>
                重试
              </Button>
            </div>
          ) : null}

          {status === 'ready' && records.length === 0 ? (
            <div className="materials-empty">
              <FileArchive size={34} strokeWidth={1.5} aria-hidden="true" />
              <h3>等待第一份资料</h3>
              <p>建议先上传教材目录、暑假作业或最近数学试卷。</p>
            </div>
          ) : null}

          {records.length > 0 ? (
            <div className="material-rows">
              {records.map((record) => (
                <article key={record.id} className="material-row">
                  <span className="material-row__icon" aria-hidden="true">
                    <FileArchive size={19} />
                  </span>
                  <div className="material-row__copy">
                    <strong>{record.name}</strong>
                    <span>
                      {record.category} · {formatFileSize(record.size)} ·{' '}
                      {new Date(record.createdAt).toLocaleDateString('zh-CN')}
                    </span>
                  </div>
                  <div className="material-row__actions">
                    <button
                      type="button"
                      aria-label={`下载 ${record.name}`}
                      onClick={() => downloadMaterial(record)}
                    >
                      <Download size={17} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      aria-label={`删除 ${record.name}`}
                      onClick={() => void remove(record.id)}
                    >
                      <Trash2 size={17} aria-hidden="true" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : null}
        </section>
      </div>
    </div>
  )
}
